import axios from 'axios';
import _ from 'lodash';
import {
  getCookie,
  setLocalStorageItem,
  getLocalStorageItem,
  removeLocalStorageItem,
} from 'helpers/Storage';
import { stripAuthProperties } from 'helpers/AuthHelper';
import { getBaseContentUrl, apiErrorHandler } from 'helpers/ApiHelper';
import { noop } from 'helpers/GlobalHelper';
import { setMVPDCookie } from 'index/AuthHelper';

export const DEVICE_TYPE = 'web';
const ENDPOINT_BUYTHROUGH_SCENARIOS = 'buythroughscenarios';
export const MVPD_SCENARIO_DIRECT = 'mvpdDirect';

const PREVIEW_PASS_60 = 'TempPass_fbcfox_60min';
const PREVIEW_PASS_5 = 'TempPass_fbcfox_5min';
export const CACHED_CHECK_AUTHENTICATION = 'CACHED_CHECK_AUTHENTICATION';
export const CACHED_USER_METADATA = 'CACHED_USER_METADATA';
export const CACHED_GET_ENTITLEMENTS = 'CACHED_GET_ENTITLEMENTS';

export const VCHIP_RATINGS = ['tv-y', 'tv-y7', 'tv-g', 'tv-pg', 'tv-14', 'tv-ma', 'nr'];

export const setLocalStorage = (key, data) => {
  try {
    setLocalStorageItem({ key, value: JSON.stringify(data) });
  } catch (error) {
    console.log('Setting cache error', error);
  }
};
export const getLocalStorage = key => {
  let item = null;
  try {
    item = JSON.parse(getLocalStorageItem({ key }));
  } catch (error) {
    console.log('Getting cache error', error);
  }
  return item;
};
export const removeLocalStorage = key => {
  try {
    removeLocalStorageItem({ key });
  } catch (error) {
    console.log('Removing cache error', error);
  }
};
export const checkAuthenticationStorage = {
  setCache: data => setLocalStorage(CACHED_CHECK_AUTHENTICATION, data),
  getCache: () => getLocalStorage(CACHED_CHECK_AUTHENTICATION),
  removeCache: () => removeLocalStorage(CACHED_CHECK_AUTHENTICATION),
};
export const userMetadataStorage = {
  setCache: data => setLocalStorage(CACHED_USER_METADATA, data),
  getCache: () => getLocalStorage(CACHED_USER_METADATA),
  removeCache: () => removeLocalStorage(CACHED_USER_METADATA),
};
export const entitlementsStorage = {
  setCache: data => setLocalStorage(CACHED_GET_ENTITLEMENTS, data),
  getCache: () => getLocalStorage(CACHED_GET_ENTITLEMENTS),
  removeCache: () => removeLocalStorage(CACHED_GET_ENTITLEMENTS),
};

export const getAccessToken = data => data?.accessToken ?? null;
const getTokenExpiration = data => data?.tokenExpiration ?? null;
const getAuthNExpiration = data => data?.authn_expire ?? null;
export const getRefreshToken = data => data?.refreshToken ?? null;

export const isTimestampExpired = timestamp => {
  const current = new Date();
  const expiration = new Date(timestamp);
  if (!expiration.getTime() || current > expiration) return true;
  return false;
};
const isTokenExpired = data => isTimestampExpired(getTokenExpiration(data));
const isAuthNExpired = data => isTimestampExpired(getAuthNExpiration(data));
export const clearCache = () => {
  console.log('Clearing clientless cache');
  checkAuthenticationStorage.removeCache();
  userMetadataStorage.removeCache();
  entitlementsStorage.removeCache();
};
export const shouldUseCache = () => {
  const data = checkAuthenticationStorage.getCache();

  if (!data) return false;

  const expired = isTokenExpired(data) || isAuthNExpired(data);

  if (expired) {
    clearCache();
    return false;
  }

  return true;
};

export function isPreviewPass(id) {
  return id === PREVIEW_PASS_5 || id === PREVIEW_PASS_60 || false;
}

const getApiUrl = options => {
  const { endpoint, authEndpointConfig, mvpdId, placeholder, placeholderValue } = options;
  const baseUrl = getBaseContentUrl();
  let baseUri = _.get(authEndpointConfig, endpoint, endpoint);
  if (placeholder && placeholderValue) baseUri = baseUri.replace(placeholder, placeholderValue);
  const completeApiUrl = baseUrl && baseUri;

  if (!completeApiUrl) return null;

  const urlParams = mvpdId && endpoint === ENDPOINT_BUYTHROUGH_SCENARIOS ? `?mvpdId=${mvpdId}` : '';
  return `${baseUrl}${baseUri}${urlParams}`;
};

const getApiHeaders = options => {
  const apiKey = _.get(options, 'authEndpointConfig.apiKey', null);
  const accessToken = _.get(options, 'accessToken', null);
  const deviceId = _.get(options, 'deviceId', null);
  const xSignatureEnabled = _.get(options, 'xSignatureEnabled', null);
  const headers = {};

  if (apiKey) headers['X-Api-Key'] = apiKey;
  if (accessToken) headers.Authorization = accessToken;
  if (deviceId) headers['x-dcg-udid'] = deviceId;
  if (xSignatureEnabled) headers['x-signature-enabled'] = xSignatureEnabled;

  return headers;
};

const sendHttpRequest = ({ url, method, params, headers } = {}) =>
  new Promise(async (resolve, reject) => {
    try {
      const result = await axios({ url, method, params, headers });
      const data = _.get(result, 'data');
      resolve(data);
    } catch (error) {
      apiErrorHandler(error);
      reject(error);
    }
  });

export const checkPreviewPass = async (options = {}) => {
  const { setMVPDToAppState, deviceId, mvpdId, account, auth } = options;
  const { refreshToken } = account;
  const setMVPDState = setMVPDToAppState || noop;

  return new Promise(async (resolve, reject) => {
    try {
      const result = await auth.getPreviewPass({
        deviceId,
        mvpdId,
        isMvpd: true,
        refreshToken,
      });
      const newAuthData = stripAuthProperties(result);

      setMVPDCookie(newAuthData);
      setMVPDState(newAuthData);
      resolve(newAuthData);
    } catch (error) {
      apiErrorHandler(error);
      reject(error);
    }
  });
};

export const getEntitlements = async (options = {}) => {
  try {
    const {
      auth,
      deviceId,
      deviceType,
      ignoreCache = false,
      mvpdCookieName,
      requestor,
      setMVPDToAppState,
    } = options;
    if (shouldUseCache() && !ignoreCache) {
      const cache = entitlementsStorage.getCache();
      if (cache) {
        console.log('Using entitlements cache');
        return cache;
      }
    }
    const setMVPDState = setMVPDToAppState || noop;
    const result = await auth.getEntitlements({
      deviceId,
      deviceType,
      requestor,
    });
    const mvpdCookie = getCookie({ name: mvpdCookieName });
    const entitlements = _.get(result, 'entitlements');
    const preauthorizedResources = entitlements && _.keys(entitlements);

    if (mvpdCookie) {
      const newMvpdCookie = {
        ...mvpdCookie,
        preauthorizedResources,
      };
      setMVPDCookie(newMvpdCookie);
      setMVPDState(newMvpdCookie);
    }

    const checkAuthenticationCache = checkAuthenticationStorage.getCache();
    if (checkAuthenticationCache) entitlementsStorage.setCache(result);
    return result;
  } catch (error) {
    apiErrorHandler(error);
    return console.log('Get entitlements error', error);
  }
};

export const getMetadata = async (options = {}) => {
  const { auth, setMVPDToAppState, mvpdCookieName } = options;
  const setMVPDState = setMVPDToAppState || noop;
  const result = await auth?.getUserMetadata();
  const mvpdCookie = getCookie({ name: mvpdCookieName });

  if (mvpdCookie) {
    const newMvpdCookie = {
      ...mvpdCookie,
      metadata: result,
    };
    setMVPDCookie(newMvpdCookie);
    setMVPDState(newMvpdCookie);
  }

  const checkAuthenticationCache = checkAuthenticationStorage.getCache();
  if (checkAuthenticationCache) userMetadataStorage.setCache(result);

  return result;
};

export const getBuythroughScenarios = async options => {
  const { mvpdId, setMVPDToAppState } = options;
  const setMVPDState = setMVPDToAppState || noop;
  const apiOptions = {
    ...options,
    endpoint: ENDPOINT_BUYTHROUGH_SCENARIOS,
    mvpdId,
    placeholder: '{packageId}',
  };
  const method = 'GET';
  const url = getApiUrl(apiOptions);
  const headers = getApiHeaders(apiOptions);

  const result = await sendHttpRequest({ url, method, headers });
  const upsellUrl = _.get(result, 'url');

  const { mvpdCookieName } = options;
  const mvpdCookie = getCookie({ name: mvpdCookieName });
  if (mvpdCookie && upsellUrl) {
    const newMvpdCookie = {
      ...mvpdCookie,
      buythroughData: result,
    };
    setMVPDCookie(newMvpdCookie);
    setMVPDState(newMvpdCookie);
  }

  return result;
};
